/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { EventEmitter } from '@angular/core';
import { SqdStatus } from './workflow-status.service';
import { Icon } from '../../common/iconSettings';
import { MergeMode, SettingType } from '../../common/enums';
import { MWSelectedFilesInFolder } from '../middleware/middleware-browse.component';
import { MWCloudItem } from '../middleware/middleware.service';
import { CloudDestination } from '../../common/constants';
import { AudioConfig } from '../components/audioConvert/xrx-audio-convert.component';
import { SummarizationConfig } from '../components/summarization/xrx-summarization.component';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  baseUrl = 'api/workflow/';
  optionLists: OptionLists;
  sqdServices: Service[];
  workflows: Workflow[];
  deleteWorkflows: EventEmitter<boolean>;

  constructor(
    private http: HttpClient,
    private userService: UserService,
  ) {
    this.deleteWorkflows = new EventEmitter();
  }

  getWorkflows() {
    // Load OptionLists and SqdServices whenever we need to get Workflows
    this.loadOptionList();
    this.loadSqdServices();
    // Only make this call if we are logged in
    if (this.userService.user) {
      return this.http.get<Workflow[]>(
        `${this.baseUrl}All/${this.userService.user.id}/${this.userService.user.accessListId}`,
      );
    }
    return null;
  }

  getSharedWorkflows() {
    // Only make this call if we are logged in
    if (this.userService.user) {
      return this.http.get<Workflow[]>(
        `${this.baseUrl}Shared/${this.userService.user.accessListId}`,
      );
    }
    return null;
  }

  getWorkflow(workflow: Workflow) {
    return this.http.get<Workflow>(
      `${this.baseUrl}${workflow.owner}/${workflow.id}`,
    );
  }

  createWorkflow(workflow: Workflow, isPrivate: boolean) {
    // new workflows require null id
    workflow.id = null;

    // Set creator to the current User
    workflow.creator = this.userService.user?.id || '';

    // If its private, then owner user, else owner is access list id
    if (isPrivate) {
      workflow.owner = workflow.creator;
    } else {
      workflow.owner = this.userService.user?.accessListId || '';
    }

    const services = workflow.services;
    // set service creator with user id or accessListId
    services.forEach((s) => {
      if (typeof s !== 'string') {
        s.creator = this.userService.user?.id || '';
        if (!isPrivate) {
          s.creator = this.userService.user?.accessListId || '';
        }
      }
    });

    const newWorkflow = JSON.parse(JSON.stringify(workflow));
    //convert full services back to PageBreak + service id
    if (
      newWorkflow.services &&
      (newWorkflow.services[0] as Service).id !== undefined
    ) {
      newWorkflow.services = (newWorkflow.services as Service[]).map((s) =>
        s.pageBreak ? '1' + s.id : '0' + s.id,
      );
    }

    return this.http.post<string>(this.baseUrl, {
      workflow: newWorkflow,
      services: services,
    });
  }

  deleteWorkflow(workflow: Workflow) {
    const owner = workflow.owner;
    const id = workflow.id;
    let serviceNames: string;

    // workflow.services contain service ids only, so get full services to get service names
    if (typeof workflow.services[0] === 'string') {
      const services: Service[] = [];
      workflow.services.forEach((x) =>
        services.push(this.getSqdService(x as string)),
      );
      serviceNames = services.map((x) => x.name).join();
    }
    // workflow.services are full service objects
    else {
      serviceNames = workflow.services.map((x) => (x as Service).name).join();
    }

    return this.http.delete<string>(
      `${this.baseUrl}${owner}/${id}/${serviceNames}`,
    );
  }

  toggleDeleteWorkflow(toggle: boolean) {
    this.deleteWorkflows.emit(toggle);
  }

  executeWorkflow(attachmentId: string, workflow: RunningWorkflow) {
    workflow.preferredLanguage = this.userService.user?.language || 'en';
    workflow.output.userName = this.userService.user?.first_name || '';

    // Verify the user is entitled to execute a job
    this.userService.validateEntitlement();

    return this.http.post<string>(
      `${this.baseUrl}Execute/${attachmentId}/${this.userService.user?.id}/${this.userService.user?.accessListId}`,
      workflow,
    );
  }

  isWorkflowWithSameNameExist(isPrivate: boolean, name: string) {
    const id = isPrivate
      ? this.userService.user?.id
      : this.userService.user?.accessListId;
    const index = this.workflows.findIndex(
      (w) => w.owner == id && w.name === name,
    );
    return index != -1;
  }

  saveWorkflow(workflow: Workflow) {
    if (workflow.isOrphan) {
      // If this is an orphaned workflow set the creator to the current user/Access List Manager/Owner
      workflow.isOrphan = false;
      workflow.creator = this.userService.user?.id || '';
    }

    // If its private, then owner user, else owner is access list id
    if (workflow.isShared) {
      workflow.owner = this.userService.user?.accessListId || '';
    } else {
      workflow.owner = this.userService.user?.id || '';
    }

    // convert full services back to PageBreak + service id
    // make a copy of the workflow before modifying the services
    const _workflow: Workflow = JSON.parse(JSON.stringify(workflow));
    const services = _workflow.services;
    _workflow.services = services.map((s) => {
      if (typeof s !== 'string') {
        return s.pageBreak ? '1' + s.id : '0' + s.id;
      }
      return s;
    });

    return this.http.put<string>(this.baseUrl, {
      workflow: _workflow,
      services: services,
    });
  }

  setWorkflowOrphanFlag(workflow: Workflow, flag: boolean) {
    workflow.isOrphan = flag;
    return this.http.put<string>(this.baseUrl, { workflow: workflow });
  }

  loadOptionList() {
    // Only make this call if we don't have the optionLists
    if (!this.optionLists) {
      this.http.get<OptionLists>(`${this.baseUrl}Optionlists`).subscribe(
        (resp) => {
          this.optionLists = resp;
        },
        () => {},
      );
    }
  }

  loadSqdServices() {
    this.http.get<Service[]>(`${this.baseUrl}AllServices`).subscribe(
      (resp) => {
        this.sqdServices = resp;
      },
      () => {},
    );
  }

  getSqdService(id: string, isSavedWorkflow?: boolean): Service {
    const pageBreak = id[0];
    const serviceId = id.slice(1);
    // make a copy of the service to return
    const service: Service = JSON.parse(
      JSON.stringify(this.sqdServices.find((s) => s.id === serviceId)),
    );
    type OptionListKey = keyof typeof this.optionLists;
    if (service && service.serviceSettings) {
      service.serviceSettings.forEach((x) => {
        if (x.optionListName) {
          x.options = this.optionLists[x.optionListName as OptionListKey];
        }

        if (x.optionListName2) {
          x.options2 = this.optionLists[x.optionListName2 as OptionListKey];
        }

        // if language selection check if the value is supported
        if (x.optionListName?.startsWith('languages') && isSavedWorkflow) {
          let lang = x.type != SettingType.PdfConvert ? x.value : x.value2;
          // check if the value returned is actually a JSON config setting that contains "language"
          if ((service.name === 'SDE_CONVERT_AUDIO' || service.name === 'SDE_EXTRACTIVE_SUMMARY') && lang?.includes("language")) {
            lang = JSON.parse(lang).language;
          }

          if (!x.options.find((item) => item.value == lang)) {
            x.error = this.userService.xdaStringsService.strings[
              'SDE_UNSUPPORTED_LANGUAGE_FMTSTR'
            ].replace('{0}', lang || '');
          }
        }

        if (service.name === 'SDE_TRANSLATE') {
          // add SDE_AUTO_DETECT to fromLanguage for Google's auto detect feature
          // if URDU is NOT in the lan list, translation engine is Abby and Auto detect is not supported.

          // uncomment out the following if clause to restore source language auto detection
          /*
          if (this.optionLists.languagesForTranslation.find(x => x.key === "SDE_URDU") !== undefined) {
            x.options = [...x.options]
            x.options.unshift({ key: 'SDE_AUTO_DETECT', value: 'auto', title: null, options: null });
          }
          */
          if (
            typeof isSavedWorkflow !== 'undefined' &&
            isSavedWorkflow === false
          ) {
            x.value = this.userService.user?.language?.slice(0, 2) || 'en';
          }
        }
      });
    }

    service.pageBreak = pageBreak === '1' ? true : false;
    return service;
  }

  transferServiceSettings(currentService: CombinedServiceSettings, nextService: CombinedServiceSettings) {
    // Transfer common service settings to the next service (e.g. language setting for Translate -> Summarization or Audio)
    //  For Combo workflows with a language setting:
    //    - Translate is followed by Summarization or Audio
    //    - Summarization is followed only by Audio

    // Determine the language setting to propagate, based on the current/next service pairing
    let language: string | undefined;

    if (
      currentService.name === 'SDE_TRANSLATE' &&
      (nextService.name === 'SDE_EXTRACTIVE_SUMMARY' || nextService.name === 'SDE_CONVERT_AUDIO')
    ) {
      language = currentService.settings[0].value2; // Translation "To" language is value2
    } else if (currentService.name === 'SDE_EXTRACTIVE_SUMMARY' && nextService.name === 'SDE_CONVERT_AUDIO' && currentService.settings[0].value) {
      const summarizeConfig = JSON.parse(currentService.settings[0].value) as SummarizationConfig;
      language = summarizeConfig.language;
    }

    if (!language) {
      return;
    }

    // Parse the next service's config to update its language
    if (nextService.settings[0].value) {
      const nextConfig = nextService.name === 'SDE_CONVERT_AUDIO'
        ? (JSON.parse(nextService.settings[0].value) as AudioConfig)
        : nextService.name === 'SDE_EXTRACTIVE_SUMMARY'
          ? (JSON.parse(nextService.settings[0].value) as SummarizationConfig)
          : null;

      // Set the language and update the settings
      if (nextConfig && nextConfig.language !== language) {
        nextConfig.language = language;
        nextService.settings[0].value = JSON.stringify(nextConfig);
      }
    }
  }
}

/**
 * WorkflowView
 */
export interface Workflow {
  id: string | null;
  creator: string;
  type: number;
  owner: string;
  seedId: string;
  parentId?: string;
  preferredLanguage: string;
  documentLanguage: string;
  name: string;
  icon: Icon;
  input: InputOptions;
  output: OutputOptions;
  services: Service[] | string[];
  status?: SqdStatus[] | null;
  isShared: boolean;
  isOrphan: boolean;
  isSingleService: boolean;
  isEncryptAndTrackDisabled: boolean;
  isDisabled: boolean;
  interimDownloaded: boolean;
  canBeDeleted?: boolean;
  isHidden?: boolean;
  parentName?: string;
}

export interface RunningWorkflow extends Omit<Workflow, 'services'> {
  workflowId: string;
  initiatedAt: Date | null;
  dateTimeStr: string | null;
  services?: RunningService[] | null;
  outputStatus?: SqdStatus[] | null;
  status?: SqdStatus[] | null;
  isMissedStatus: boolean;
  interimContinued: boolean;
  pageCount?: string;
  waitToBeCanceled: boolean; // client side only. True until returning from executeWorkflow.
  executionId: string; // used for looking up execution Subscription to call unsubscrible() on CancelWorkflow.
  forceEnableCancel: boolean; // used for enable cancel button in Fax workflow after certain time elapsed.
  printJobId: string; // used to keep track of the jobId if a print job is submitted to the device
}

export interface InputOptions {
  fileId: string;
  scanOptions: ScanOptions[];
  isScannedInput: boolean;
  isCloudInput: boolean;
  sendInput: boolean;
  sendInterim: boolean;
  sendCloudInput: boolean;
  sendCloudInterim: boolean;
  pauseInterim: boolean;
  editUsed: boolean;
  cloudRepoOptions?: CloudRepo;
  description: string;
  detail: string;
  maxPageLimit: number;
  maxPageMessage: string;
  maxSizeLimit: number;
  maxSizeMessage: string;
  maxConcatenatedDocument: number;
  maxInsertedDocument: number;
  mergeDocuments: MergeDocument[];
  mergeMode: MergeMode;
  selectPages?: boolean;
  selectedPages: string;
  selectedPagesError: string;
  selectedPagesValidation: RegExp;
  selectedPagesValidationError: string;
  insertPageNumber?: number;
  cloudRepoInputs?: CloudFileInfo[];
  pageCount?: string;
  availableFileInputs: number;
  cloudRepos: CloudDestination[];
  currentCloudRepo: CloudRepo | null;
  fileTypes: {
    raw: null;
    asAccept: null;
    asString: null;
  };
}
export interface ScanOptions {
  key: string;
  options: string;
  title: string;
  value: string;
}

export interface CloudRepo {
  connector: string;
  region?: string;
  sitePath?: MWCloudItem[] | null;
  dir?: MWCloudItem | null;
  files?: MWCloudItem | MWSelectedFilesInFolder[] | null;
  access?: string | null;
  refresh?: string;
  tenantId?: string;
}

export interface CloudFileInfo {
  connector: string;
  region?: string;
  access?: string | null;
  refresh?: string;
  tenantId?: string;
  sitePath?: MWCloudItem[] | null;
  dir?: MWCloudItem | null;
  file?: MWCloudItem | null;
  // SQD doc properties
  fileId?: string;
  fileFormat?: string;
  outputFileName?: string;
  pageRange?: string;
  insertPageNumber?: number;
  name?: string;
}

export interface MergeDocument {
  file: WCFFileType | null;
  fileName: string;
  fileId: string;
  fileFormat: string;
  insertPageNumber: number;
}

export interface BatchInputDocument {
  file: WCFFileType;
  fileName: string;
  fileFormat: string;
  outputFileName: string;
}

export interface OutputOptions {
  settingTitle: string;
  file: WCFFileType;
  userName?: string;
  fileName: string | null;
  emailAddresses?: string;
  fromEmailAddress?: string;
  encryptionPassword?: string | null;
  sendEncryptedEmail: boolean;
  sendEncryptedEmailLabel: string | null;
  passwordProtection: boolean;
  passwordProtectionEnabled: boolean;
  ouputFileNameEditable: boolean;
  dateTimeSelectionEnabled: boolean;
  emailOptions?: EmailOptions[];
  printOptions?: PrintOptions[] | null;
  cloudRepoOptions?: CloudRepo | null;
  cloudRepoSourceLinkEnabled: boolean;
  outputTypeTitle: string;
  outputType: number;
  outputRequired: boolean;
  outputOptions?: OutputOption[];
  isCompressed?: boolean;
  currentCloudRepo: CloudRepo | null;
  addDateTime: boolean;
  datetimestr: string | null;
  fileNameError: string;
  fileNameValidation: RegExp;
  fileTypes: {
    raw: FileType | null;
    asArray: { key: string; value: string }[];
    extension: { key: string; value: string };
  };
  selector: { items: Item[] };
  email: string;
  confirmEmail: string;
  emailSendOnErrorValue: boolean;
}

export interface Item {
  label: string;
  value: string;
  glyph: string;
  isDefault: boolean;
  isOn: boolean;
  disabled?: boolean;
}

export interface OutputOption {
  key: string;
  value: string;
}

export interface EmailOptions {
  key: string;
  options: string | null;
  title: string | null;
  value: string;
  visible: string;
  sourceLink?: string;
}

export interface PrintOptions {
  key: string;
  options: OptionListOptions[];
  title: string;
  value: string;
  visible: boolean;
}

export interface InputFileInfo extends File {
  file: WCFFileType | null;
  name: string;
  fileId: string;
  fileFormat: string;
  insertPageNumber: number;
  outputFileName?: string;
}

export interface WCFFileType {
  file: WCFFileType | null;
  name: string;
  fileName: string;
  fileId: string;
  fileFormat: string;
  insertPageNumber: number;
  extension?: FileType;
  appendDateTimeToFilename?: boolean;
  description?: string;
}

export interface Service {
  id: string;
  seedId: string;
  creator: string;
  name: string;
  parentId: string;
  availableFileInputs: FileType;
  availableFileOutputs: FileType;
  inputSettings?: Parameter[] | null;
  serviceSettings?: Parameter[] | null;
  outputFileFormat: string;
  outputDescription: string;
  pageBreak?: boolean;
  version?: number;
}

export interface RunningService extends Omit<
  Service,
  | 'creator'
  | 'availableFileInputs'
  | 'availableFileOutputs'
  | 'inputSettings'
  | 'serviceSettings'
  | 'outputDescription'
> {
  parentId: string;
  outputFileFormat: string;
  status?: SqdStatus[] | null;
  inputSettings?: RunningParameter[] | null;
  serviceSettings?: RunningParameter[] | null;
}

export interface Parameter {
  name: string;
  name2?: string;
  label: string;
  label2?: string;
  maxlength?: number;
  optionListName: string;
  optionListName2: string;
  options: OptionListOptions[];
  options2: OptionListOptions[];
  pattern?: string;
  placeholder: string;
  placeholder2?: string;
  required: boolean;
  selectedValue: OptionListOptions;
  selectedValue2: OptionListOptions;
  setDocumentLanguage: string;
  toLanguage: string;
  type: SettingType;
  error?: string;
  glyph?: string;
  icon?: string;
  icon2?: string;
  isValid?: boolean;
  validations?: string;
  value?: string;
  value2?: string;
  checked?: boolean;
  matchesToLanguage?: boolean;
  description?: string;
}

export interface RunningParameter {
  value?: string;
  value2?: string;
  name: string;
  name2?: string;
  type: number;
  setDocumentLanguage: string;
}

export interface CombinedServiceSettings {
  name: string;
  validations: Record<string, boolean>;
  settings: Parameter[];
}

export interface OptionListOptions {
  key: string;
  value: string | boolean;
  title?: string | null;
  glyph?: string;
  icon?:
    | string
    | {
        image?: string;
      };
  options?: OptionListOptions[] | null;
  isDefault?: boolean;
  checked?: boolean;
  matchesToLanguage?: boolean;
}

export interface OptionLists {
  languagesForAudio: OptionListOptions[];
  languagesForHandwriting: OptionListOptions[];
  languagesForMSOffice: OptionListOptions[];
  languagesForTranslationInput: OptionListOptions[];
  languagesForTranslationOutput: OptionListOptions[];
  summarizationLength: OptionListOptions[];
  summarizationDomain: OptionListOptions[];
  faxCountryCodes: OptionListOptions[];
}

export enum FileType {
  undefined = 0,
  bmp = 1,
  gif = 2,
  jpg = 4,
  jpeg = jpg,
  png = 8,
  tif = 16,
  tiff = tif,
  sevenz = 32,
  zip = 64,
  tar = 128,
  rar = 256,
  email = 512,
  eml = 1024,
  msg = 2048,
  mp3 = 4096,
  txt = 8192,
  pdf = 16384,
  docx = 32768,
  pptx = 65536,
  xlsx = 131072,
  doc = 262144,
  ppt = 524288,
  xls = 1048576,
  rtf = 2097152,
  html = 4194304,
}

export enum OutputType {
  none = 0,
  download = 1,
  email = 2,
  cloud = 4,
  print = 8,
}
